import { VOICE_AT_RISK_INACTIVITY_DAYS } from '@reach/shared-types/voice-tenant-overview';
import React from 'react';
import { Button } from '../../../components/ui/button';
import { InfoTooltip } from '../../../components/ui/InfoTooltip';
import { VoiceOverviewTenantRow } from './VoiceOverviewTenantRow';
import { VOICE_OVERVIEW_PAGE_SIZE, useVoiceTenantOverview } from './useVoiceTenantOverview';
import styles from './VoiceOverviewTenantsTable.module.css';

const AT_RISK_HEADER_TOOLTIP = `Flagged when an active tenant’s last call was more than ${VOICE_AT_RISK_INACTIVITY_DAYS} days ago, or its first agent was created more than ${VOICE_AT_RISK_INACTIVITY_DAYS} days ago and it has no recorded calls. Select a badge for the specific reason.`;

export const VoiceOverviewTenantsTable: React.FC = () => {
  const {
    data,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    currentPage,
    goToNextPage,
    goToPreviousPage,
  } = useVoiceTenantOverview();

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingPulse}>
          <div className={`${styles.loadingBar} ${styles.loadingSkeletonHeader}`} />
          <div className={`${styles.loadingBar} ${styles.loadingSkeletonRow}`} />
          <div className={`${styles.loadingBar} ${styles.loadingSkeletonRow}`} />
          <div className={`${styles.loadingBar} ${styles.loadingSkeletonRow}`} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorTitle}>Error loading Assist tenants</div>
        <div className={styles.errorMessage}>{error.message || 'An unexpected error occurred'}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return <div className={styles.empty}>No tenants have Assist agents yet.</div>;
  }

  const startRank = (currentPage - 1) * VOICE_OVERVIEW_PAGE_SIZE + 1;
  const endRank = startRank + data.length - 1;

  return (
    <div className={styles.tableWrapper}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Tenant Name</th>
              <th scope="col">Status</th>
              <th scope="col">Last Call</th>
              <th scope="col">
                <span className={styles.thWithTooltip}>
                  At Risk
                  <InfoTooltip label={AT_RISK_HEADER_TOOLTIP}>{AT_RISK_HEADER_TOOLTIP}</InfoTooltip>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((tenant, index) => (
              <VoiceOverviewTenantRow key={tenant.tenantId} rank={startRank + index} tenant={tenant} />
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <div className={styles.pageInfo}>
          Showing {startRank}–{endRank}
        </div>
        <div className={styles.pageControls}>
          <Button
            variant="outline"
            size="sm"
            onClick={goToPreviousPage}
            disabled={currentPage === 1 || isFetching}>
            Previous
          </Button>
          <span>Page {currentPage}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={goToNextPage}
            disabled={!hasNextPage || isFetching}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
